import { PHASE } from "../../common/constants.ts";
import { league } from "../core/index.ts";
import { idb } from "../db/index.ts";
import { g, helpers, logEvent } from "../util/index.ts";
import { getClient, getLeagueIdForLid } from "./index.ts";

// ============================================================================
// v16: multiplayer expansion draft. When a scheduled real-league expansion
// (e.g. Dallas 1980) fires during the runner's sim, the segment pauses and
// publishes a snapshot at PHASE.EXPANSION_DRAFT (protection). Every human then
// protects their own players on the normal Protect Players page - each edit is
// synced as a 'protect' event so the runner (and everyone else) has the same
// protection lists. When everyone readies up again, the runner resolves the
// expansion draft (auto-protecting any human who never chose) and continues
// the segment. See simRunner.ts for the pause/resume flow.
// ============================================================================

export type ProtectEventPayload = {
	tid: number;
	pids: number[];
};

// Deterministic application of a protect event (all clients, sender included).
// Guarded so a stale event (expansion already resolved) can never corrupt
// state, and pids are validated against the players actually on that team.
export const applyProtectEvent = async (payload: ProtectEventPayload) => {
	if (g.get("phase") !== PHASE.EXPANSION_DRAFT) {
		return;
	}
	const expansionDraft = helpers.deepCopy(g.get("expansionDraft"));
	if (expansionDraft.phase !== "protection") {
		return;
	}

	const teamPids = new Set(
		(await idb.cache.players.indexGetAll("playersByTid", payload.tid)).map(
			(p) => p.pid,
		),
	);
	const pids = payload.pids
		.filter((pid) => teamPids.has(pid))
		.slice(0, expansionDraft.numProtectedPlayers);

	expansionDraft.protectedPids[payload.tid] = pids;
	await league.setGameAttributes({ expansionDraft });
};

// Called (debounced) whenever a human edits their protected-players list on
// the Protect Players page. Fire-and-forget like mpMaybeSyncLineup.
let protectSyncTimer: ReturnType<typeof setTimeout> | undefined;
export const mpMaybeSyncProtect = () => {
	if (protectSyncTimer) {
		clearTimeout(protectSyncTimer);
	}
	protectSyncTimer = setTimeout(async () => {
		protectSyncTimer = undefined;
		try {
			const lid = g.get("lid");
			if (typeof lid !== "number") {
				return;
			}
			const leagueId = await getLeagueIdForLid(lid);
			if (!leagueId) {
				return;
			}
			if (g.get("phase") !== PHASE.EXPANSION_DRAFT) {
				return;
			}
			const expansionDraft = g.get("expansionDraft");
			if (expansionDraft.phase !== "protection") {
				return;
			}
			const tid = g.get("userTid");
			const payload: ProtectEventPayload = {
				tid,
				pids: expansionDraft.protectedPids[tid] ?? [],
			};
			const { data, error } = await getClient().rpc("submit_protect", {
				p_league_id: leagueId,
				p_payload: payload,
			});
			if (error) {
				throw new Error(error.message);
			}
			if (data.status !== "ok") {
				throw new Error(data.status);
			}
		} catch (error) {
			logEvent({
				type: "error",
				text: `Syncing your protected players failed (${(error as Error).message}) - your team may be auto-protected instead.`,
				saveToDb: false,
			});
		}
	}, 1000);
};
